import type { Activity, ActivityAnalysisState, ActivityType, VibrationMode } from '@/types/activity';
import { ACTIVITY_TYPES, VIBRATION_MODES } from '@/types/activity';
import { warn } from '@/utils/logger';

export interface ApiActivity {
  id: string;
  type: string;
  content?: {
    title?: string;
    'activity-id'?: string;
  };
  streamId?: string;
  streamIds?: string[];
  time?: number;
  created?: number;
  duration?: number | null;
  trashed?: boolean;
}

export function extractActivityType(streamIds: string[] | undefined): ActivityType {
  if (!streamIds) return 'generic';

  for (const streamId of streamIds) {
    const normalized = streamId.toLowerCase().replace(/_/g, '-');
    if (ACTIVITY_TYPES.includes(normalized as ActivityType)) {
      return normalized as ActivityType;
    }
  }

  return 'generic';
}

export function extractVibrationMode(
  streamId: string | undefined,
  streamIds: string[] | undefined
): VibrationMode {
  if (streamId) {
    const normalized = streamId.toLowerCase().replace(/_/g, '-');
    if (VIBRATION_MODES.includes(normalized as VibrationMode)) {
      return normalized as VibrationMode;
    }
  }

  if (streamIds) {
    for (const id of streamIds) {
      const normalized = id.toLowerCase().replace(/_/g, '-');
      if (VIBRATION_MODES.includes(normalized as VibrationMode)) {
        return normalized as VibrationMode;
      }
    }
  }

  return 'none';
}

function unixToISO(unixSeconds: number | undefined): string {
  if (!unixSeconds) return new Date().toISOString();
  return new Date(unixSeconds * 1000).toISOString();
}

export function extractActivityStreamId(streamIds: string[] | undefined): string | undefined {
  if (!streamIds) return undefined;

  const activityId = streamIds.find(
    (id) => id === 'activity' || id.toLowerCase().includes('activity')
  );

  if (!activityId) {
    warn('[API] extractActivityStreamId: No activity stream found in streamIds:', streamIds);
  }

  return activityId;
}

export function extractActivityAnalysisState(
  streamIds: string[] | undefined
): ActivityAnalysisState | undefined {
  if (!streamIds) return undefined;

  const normalizedStreamIds = streamIds.map((streamId) => streamId.toLowerCase().replace(/_/g, '-'));

  if (normalizedStreamIds.includes('failed')) {
    return 'failed';
  }

  if (normalizedStreamIds.includes('unanalyzed') || normalizedStreamIds.includes('unanalysed')) {
    return 'unanalyzed';
  }

  return undefined;
}

export function transformApiActivity(
  raw: ApiActivity,
  patientId: string,
  patientName: string,
  patientUsername: string,
  index: number
): Activity {
  return {
    id: raw.id || `activity-${patientId}-${index}`,
    activityStreamId: extractActivityStreamId(raw.streamIds),
    analysisState: extractActivityAnalysisState(raw.streamIds),
    streamIds: raw.streamIds,
    patientId,
    patientName,
    patientUsername,
    type: extractActivityType(raw.streamIds),
    title: raw.content?.title || extractActivityType(raw.streamIds),
    vibrationMode: extractVibrationMode(raw.streamId, raw.streamIds),
    timestamp: unixToISO(raw.time || raw.created),
    duration: raw.duration ?? undefined,
  };
}
